import { Container, Grid } from "@mui/material";
import { endOfDay } from "date-fns";
import { isEqual } from "lodash";
import { FC, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  HistoryFilter,
  HistoryFilterDateTypes,
  HistoryFilterStorageKey,
  defaultHistoryFilter,
} from "models/HistoryFilter";
import { useAppDispatch, useAppSelector } from "store";
import {
  resetHistoryFilter,
  selectHistoryFilter,
  setHistoryFilter,
} from "store/slices/history.store";

import { AsolviDatePicker } from "components/AsolviDatePicker";
import { DateFilterType, FilterDateSelect } from "components/FilterDateSelect";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";

type HistoryFilterDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose: (dirty: boolean) => void;
};

export const HistoryFilterDialog: FC<HistoryFilterDialogProps> = (props) => {
  const { open, setOpen, onClose } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const filter = useAppSelector(selectHistoryFilter);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const dateOptions: DateFilterType[] = useMemo(() => {
    var options = [
      {
        label: intl.formatMessage({ id: "filter.last7days" }),
        value: "week",
      },
      {
        label: intl.formatMessage({ id: "filter.lastMonth" }),
        value: "1month",
      },
      {
        label: intl.formatMessage({ id: "filter.specificDate" }),
        value: "specificDate",
      },
    ];
    return options;
  }, [intl]);

  const setFilter = useCallback(
    (filter: HistoryFilter) => {
      setIsDirty(true);
      dispatch(setHistoryFilter({ historyFilter: filter }));
    },
    [dispatch]
  );

  const setDate = useCallback(
    (type: HistoryFilterDateTypes) => {
      var specificDate = null;
      if (type === "specificDate") specificDate = filter.specificDate;
      setFilter({
        ...filter,
        typeDate: type,
        specificDate: specificDate,
      });
    },
    [filter, setFilter]
  );
  const setSpecificDate = useCallback(
    (value: Date | null) => {
      setFilter({
        ...filter,
        specificDate: value ? endOfDay(value) : null,
        typeDate: "specificDate",
      });
    },
    [filter, setFilter]
  );

  const resetFilter = useCallback(() => {
    if (!isEqual(filter, defaultHistoryFilter)) {
      setIsDirty(true);
    }
    dispatch(resetHistoryFilter());
  }, [dispatch, filter]);

  const saveFilter = useCallback(() => {
    localStorage.setItem(HistoryFilterStorageKey, JSON.stringify(filter));
  }, [filter]);

  const handleClose = useCallback(() => {
    const dirty = isDirty;
    saveFilter();
    setOpen(false);
    setIsDirty(false);
    onClose(dirty);
  }, [isDirty, onClose, saveFilter, setOpen]);

  return (
    <FullScreenDialog
      title={intl.formatMessage({ id: "filter.setFilters" })}
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      child={
        <Container sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FilterDateSelect value={filter.typeDate} options={dateOptions} onChange={setDate} />
            </Grid>
            {filter.typeDate === "specificDate" && (
              <Grid item xs={12}>
                <AsolviDatePicker
                  data-testid="HistoryFilterDialog-DatePickerFilter"
                  onChange={(value) => {
                    setSpecificDate(value);
                  }}
                  value={filter.specificDate}
                  label={intl.formatMessage({ id: "filter.specificDate" })}
                  clearable
                />
              </Grid>
            )}
            <Grid item xs={12} mt={2}>
              <SecondaryButton onClick={resetFilter} data-testid="HistoryFilterDialog-ResetButton">
                <FormattedMessage id="filter.reset" />
              </SecondaryButton>
            </Grid>
            <Grid item xs={12}>
              <PrimaryButton data-testid="HistoryFilterDialog-SubmitButton" onClick={handleClose}>
                <FormattedMessage id={"general.viewJobs"} />
              </PrimaryButton>
            </Grid>
          </Grid>
        </Container>
      }
    />
  );
};
